import type { PricingCourseRow } from '../../hooks/usePricing.js';
import { activeScholarship, isNegativeNet, scholarshipLabel } from '../../lib/pricing.js';
import { centsToAudDisplay } from '../../lib/money.js';
import { indicativeTotalCents } from '../../lib/quotes.js';
import { Button } from '../ui/Button.js';

/**
 * The BUILDER's line table (Brief 7b): the courses picked so far, in pick order,
 * with a running indicative total.
 *
 * Deliberately NOT shared with the quote detail screen. A locked line is a
 * different type (non-nullable money, a 'not_set' scholarship kind, a server
 * lineOrder, no courseId), and a `mode` prop would be exactly the "flag to
 * default wrong" the quotes API's own docblock refuses. The detail's table is
 * local to that screen.
 *
 * Array order IS pick order IS pathway order — the server assigns
 * lineOrder = index + 1 from the payload.
 */
export function QuoteLinesTable({
  lines,
  onRemove,
  disabled,
}: {
  lines: readonly PricingCourseRow[];
  onRemove: (courseId: string) => void;
  disabled?: boolean;
}) {
  if (lines.length === 0) {
    return (
      <p className="surface px-5 py-6 text-sm text-muted">
        No courses yet. Add at least one from the list above.
      </p>
    );
  }

  return (
    <div>
      <div className="surface p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 border-b border-line">
            <tr className="text-left text-muted text-xs uppercase tracking-wider">
              <th className="px-5 py-3 font-medium">#</th>
              <th className="px-5 py-3 font-medium">Course</th>
              <th className="px-5 py-3 font-medium text-right">Fee</th>
              <th className="px-5 py-3 font-medium text-right">Scholarship</th>
              <th className="px-5 py-3 font-medium text-right">Net fee</th>
              <th className="px-5 py-3 font-medium text-right">Remove</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {lines.map((c, i) => {
              const net = c.netFeeCents ?? null;
              const sch = activeScholarship(c.scholarship);
              return (
                <tr key={c.courseId} className="align-top">
                  <td className="px-5 py-3 text-sm text-muted">{i + 1}</td>
                  <td className="px-5 py-3 min-w-[16rem]">
                    <span className="block text-sm text-ink">{c.name}</span>
                    <span className="block text-xs text-muted">{c.providerName}</span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-navy">
                    {centsToAudDisplay(c.agencyFee ? c.agencyFee.tuitionCents : null)}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-ink">
                    {sch ? scholarshipLabel(sch) : '—'}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <span
                      className={
                        net !== null && isNegativeNet(net)
                          ? 'text-sm text-danger'
                          : 'text-sm font-medium text-navy'
                      }
                    >
                      {centsToAudDisplay(net)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={disabled}
                      onClick={() => onRemove(c.courseId)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-cream/60 border-t border-line">
            <tr>
              <td className="px-5 py-3 text-sm text-ink" colSpan={4}>
                Indicative total
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap text-sm font-medium text-navy">
                {centsToAudDisplay(indicativeTotalCents(lines))}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-2 text-xs text-muted">
        Indicative — final figures are locked when you save.
      </p>
    </div>
  );
}
